import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Form } from 'react-bootstrap';
import { adminError, ssoReadinessMessage } from './api';
import { useAuth } from '../Auth/AuthProvider';
import { PageTitle, ResourceState, SsoDetails, useAdminApi, useAdminResource } from './shared';

export default function Sso() {
  const api = useAdminApi();
  const { user } = useAuth();
  const resource = useAdminResource(useCallback(async () => {
    const [status, readiness] = await Promise.all([api.ssoStatus(), api.ssoReadiness()]);
    return { ...status, readiness };
  }, [api]));
  const [issuer, setIssuer] = useState(''); const [clientId, setClientId] = useState('');
  const [scopes, setScopes] = useState(''); const [secret, setSecret] = useState('');
  const [busy, setBusy] = useState(false); const [error, setError] = useState('');
  const [notice, setNotice] = useState(''); const [testResult, setTestResult] = useState(null);
  const [initialized, setInitialized] = useState(false);
  useEffect(() => {
    if (resource.data && !initialized) {
      setIssuer(resource.data.issuer || ''); setScopes(resource.data.scopes || ''); setInitialized(true);
    }
  }, [resource.data, initialized]);

  const save = async event => {
    event.preventDefault(); setBusy(true); setError(''); setNotice(''); setTestResult(null);
    try {
      await api.saveSso({ issuer: issuer.trim(), clientId: clientId.trim(), clientSecret: secret, scopes: scopes.trim() || null });
      setSecret('');
      setNotice('SSO configuration saved. Test issuer reachability separately. Tenant activation remains controlled by the backend.');
      await resource.reload();
    } catch (failure) { setError(adminError(failure)); }
    finally { setSecret(''); setBusy(false); }
  };
  const test = async () => {
    setBusy(true); setError(''); setNotice(''); setTestResult(null);
    try {
      const result = await api.testSso();
      // Do not echo provider/error payloads. This contract only tests discovery reachability.
      if (result.success) setTestResult('OIDC issuer is reachable. This is not a full sign-in verification.');
      else setError('OIDC issuer reachability test failed. Check the saved configuration.');
      await resource.reload();
    } catch (failure) { setError(adminError(failure)); }
    finally { setBusy(false); }
  };
  const provision = async () => {
    setBusy(true); setError(''); setNotice(''); setTestResult(null);
    try {
      await api.provisionOidc(user.id);
      setNotice('SSO is enabled for your user. Your LOCAL identity is preserved. A Platform Admin still controls the tenant authentication mode.');
      await resource.reload();
    } catch (failure) { setError(adminError(failure)); }
    finally { setBusy(false); }
  };
  return <section className="ta-page"><PageTitle title="Single sign-on" />
    <p>Save configuration and test the saved issuer separately. These actions do not switch the tenant’s authentication mode or activate the tenant.</p>
    {error && <Alert variant="danger" role="alert">{error}</Alert>}
    {notice && <Alert variant="success" role="status">{notice}</Alert>}
    {testResult && <Alert variant="info" role="status">{testResult}</Alert>}
    <ResourceState resource={resource}><Card><Card.Body><h2>Current SSO status</h2><SsoDetails sso={resource.data} />
      <Button disabled={busy} variant="outline-secondary" onClick={test}>Test saved configuration</Button>
      <h2>Your SSO readiness</h2>
      <p>OIDC identity: {resource.data?.readiness.currentUserOidcProvisioned ? 'Provisioned' : 'Not provisioned'}</p>
      <p>{resource.data?.readiness.readyForSso ? 'Ready for a Platform Admin to activate SSO.' : ssoReadinessMessage(resource.data?.readiness.reason)}</p>
      {!resource.data?.readiness.currentUserOidcProvisioned && <Button disabled={busy || !user.id || !resource.data?.configured || !resource.data?.enabled || !resource.data?.lastTestedAt} onClick={provision}>Enable SSO for this user</Button>}
    </Card.Body></Card></ResourceState>
    <Card><Card.Body><h2>Configure SSO</h2><Form onSubmit={save} aria-label="SSO configuration">
      <Form.Group controlId="sso-issuer"><Form.Label>Issuer URL</Form.Label><Form.Control type="url" maxLength={500} required value={issuer} disabled={busy} onChange={e => setIssuer(e.target.value)} aria-describedby="issuer-help" /><p id="issuer-help" className="ta-hint">Use the HTTPS issuer URL supplied by your identity provider.</p></Form.Group>
      <Form.Group controlId="sso-client-id"><Form.Label>Client ID</Form.Label><Form.Control maxLength={500} required autoComplete="off" value={clientId} disabled={busy} onChange={e => setClientId(e.target.value)} aria-describedby="client-help" /><p id="client-help" className="ta-hint">The API does not return the saved client ID. Enter the client ID when saving configuration.</p></Form.Group>
      <Form.Group controlId="sso-scopes"><Form.Label>Scopes</Form.Label><Form.Control maxLength={1000} value={scopes} disabled={busy} onChange={e => setScopes(e.target.value)} aria-describedby="scopes-help" /><p id="scopes-help" className="ta-hint">Leave blank to use the backend default scopes.</p></Form.Group>
      <Form.Group controlId="sso-secret"><Form.Label>Client secret</Form.Label><Form.Control type="password" maxLength={2000} autoComplete="new-password" required value={secret} disabled={busy} onChange={e => setSecret(e.target.value)} aria-describedby="secret-help" /><p id="secret-help" className="ta-hint">A secret is required for every save. Stored secrets are never returned. This field is cleared after submission.</p></Form.Group>
      <Button type="submit" disabled={busy || resource.loading}>{busy ? 'Please wait…' : 'Save configuration'}</Button>
    </Form></Card.Body></Card>
  </section>;
}
